import React, {
  forwardRef,
  Ref,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Form as BaseForm,
  FormItem,
  Input,
  InputRef,
  Select,
} from '../../atoms';
import { PhoneInput, phoneInputValidations } from '../../PhoneInput';
import { next, hooks } from '@vette/frontend-utils';
import { vetter } from '@vette/common-utils';
import { Col, FormInstance, Grid, Row } from 'antd';
import { validations } from './validations';
import { FormValues } from './types';
import * as S from './styles';

type Props = {
  form: FormInstance<FormValues>;
  header: React.ReactNode | string;
  onAuthenticate: () => Promise<boolean>;
  onVerify: () => Promise<void>;
  buttonText: string;
  verificationDisabled?: boolean;
  phoneNumberLabel?: string;
  gatherChannel?: boolean;
  gatherName?: boolean;
  isVerificationLoading?: boolean;
};

const RESEND_TIMEOUT_SECONDS = 30;

export type FormRef = {
  focusVerificationCodeInput: () => void;
};

export const Form = forwardRef(
  (
    {
      form,
      header,
      onAuthenticate,
      onVerify,
      verificationDisabled,
      buttonText,
      phoneNumberLabel,
      gatherChannel,
      gatherName,
      isVerificationLoading,
    }: Props,
    ref?: Ref<FormRef>
  ) => {
    const { xs } = Grid.useBreakpoint();
    const verificationCodeInputRef = useRef<InputRef>(null);
    const verifyOnEnter = hooks.useEnterPressed(onVerify);
    const inputPhone = BaseForm.useWatch(['phone'], form);
    const channel = BaseForm.useWatch(['channel'], form);
    const isValidPhone = useMemo(() => {
      return inputPhone && inputPhone.length === 12;
    }, [inputPhone]);

    const [sendOTPTimeout, setSendOTPTimeout] = useState<number>(0);

    const getVerificationHint = (): string => {
      if (verificationDisabled) return '';
      const hasVerificationError = form
        .getFieldsError(['verificationCode'])
        .some(({ errors }) => errors.length > 0);
      if (hasVerificationError) return '';
      return 'Enter the verification code we sent to your phone';
    };

    useImperativeHandle(ref, () => ({
      focusVerificationCodeInput: () => {
        verificationCodeInputRef.current?.focus();
      },
    }));

    return (
      <BaseForm form={form}>
        <next.ClientSide>
          <S.Wrapper $xs={xs} align="stretch">
            {header}

            {gatherName && (
              <S.Col span={12}>
                <Row justify="space-between" gutter={[24, 24]}>
                  <Col sm={12} xs={24}>
                    <FormItem
                      label="First name"
                      name="firstName"
                      rules={validations.firstName}
                    >
                      <Input aria-label="first name" />
                    </FormItem>
                  </Col>
                  <Col sm={12} xs={24}>
                    <FormItem
                      label="Last name"
                      name="lastName"
                      rules={validations.lastName}
                    >
                      <Input aria-label="last name" />
                    </FormItem>
                  </Col>
                </Row>
              </S.Col>
            )}

            <FormItem
              label={phoneNumberLabel || 'Phone number'}
              name="phone"
              validateTrigger={['onChange']}
              rules={phoneInputValidations}
            >
              <PhoneInput
                onEnterKeyPress={() => isValidPhone && onAuthenticate()}
                inputProps={{ 'aria-label': 'phone number' }}
              />
            </FormItem>

            <FormItem shouldUpdate noStyle>
              {() => (
                <Button
                  id="verification-button"
                  aria-label="send verification code"
                  size="small"
                  loading={isVerificationLoading}
                  actionType="secondary"
                  disabled={!isValidPhone || sendOTPTimeout > 0}
                  onClick={async () => {
                    const isSuccessful = await onAuthenticate();
                    if (isSuccessful) {
                      setSendOTPTimeout(RESEND_TIMEOUT_SECONDS);
                      const interval = setInterval(() => {
                        setSendOTPTimeout(val => val - 1);
                      }, 1000);
                      setTimeout(() => {
                        clearInterval(interval);
                      }, (RESEND_TIMEOUT_SECONDS + 1) * 1000);
                    }
                  }}
                >
                  {sendOTPTimeout > 0
                    ? `Resend verification code in ${sendOTPTimeout}s`
                    : 'Send verification code'}
                </Button>
              )}
            </FormItem>

            <FormItem
              label="Verification code"
              name="verificationCode"
              validateTrigger={['onChange']}
              rules={validations.code}
              shouldUpdate
            >
              <Input
                ref={verificationCodeInputRef}
                id="verificationCode"
                size="large"
                onKeyUp={verifyOnEnter}
                disabled={verificationDisabled}
                optionalText={getVerificationHint()}
                aria-label="verification code"
              />
            </FormItem>

            {gatherChannel && (
              <FormItem
                label="How did you hear about Vette?"
                name="channel"
                rules={[
                  {
                    required: true,
                    message: 'Please fill out information',
                  },
                ]}
              >
                <Select
                  placeholder="Please choose one"
                  size="large"
                  option={vetter.VetterChannels}
                  disabled={verificationDisabled}
                  aria-label="how did you hear about Vette dropdown"
                />
              </FormItem>
            )}

            {channel === 'Other' && (
              <FormItem
                label="Please specify"
                name="otherChannel"
                rules={[
                  {
                    required: true,
                    message: 'Please fill out information',
                  },
                ]}
                validateTrigger={['onChange']}
              >
                <Input aria-label="other channel" />
              </FormItem>
            )}

            <Button
              id="submit-button"
              htmlType="submit"
              size="large"
              actionType="primary"
              disabled={verificationDisabled}
              onClick={onVerify}
              aria-label="submit form"
            >
              {buttonText}
            </Button>
          </S.Wrapper>
        </next.ClientSide>
      </BaseForm>
    );
  }
);
